import React from 'react';
import { Link } from 'react-router-dom';

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', color: '#1e293b', fontFamily: 'inherit' },
  nav: { background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 },
  logo: { fontSize: 22, fontWeight: 800, color: '#2563eb', textDecoration: 'none' },
  navLinks: { display: 'flex', gap: 12, alignItems: 'center' },
  loginBtn: { background: 'transparent', border: '1.5px solid #2563eb', color: '#2563eb', padding: '8px 20px', borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: 'pointer', textDecoration: 'none' },
  registerBtn: { background: '#2563eb', color: '#fff', border: 'none', padding: '9px 22px', borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: 'pointer', textDecoration: 'none' },
  hero: { maxWidth: 720, margin: '0 auto', padding: '80px 24px 60px', textAlign: 'center' },
  heroTitle: { fontSize: 48, fontWeight: 800, color: '#1e293b', lineHeight: 1.15, marginBottom: 20 },
  heroAccent: { color: '#2563eb' },
  heroSub: { fontSize: 20, color: '#64748b', lineHeight: 1.6, marginBottom: 40, maxWidth: 540, margin: '0 auto 40px' },
  heroBtns: { display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 16 },
  primaryBtn: { background: '#2563eb', color: '#fff', border: 'none', padding: '14px 32px', borderRadius: 10, fontWeight: 700, fontSize: 17, cursor: 'pointer', textDecoration: 'none' },
  secondaryBtn: { background: '#fff', color: '#2563eb', border: '2px solid #2563eb', padding: '14px 32px', borderRadius: 10, fontWeight: 700, fontSize: 17, cursor: 'pointer', textDecoration: 'none' },
  freeNote: { color: '#94a3b8', fontSize: 14 },
  features: { background: '#fff', padding: '64px 24px' },
  featuresInner: { maxWidth: 960, margin: '0 auto' },
  featuresTitle: { fontSize: 32, fontWeight: 800, textAlign: 'center', marginBottom: 48, color: '#1e293b' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 },
  featureCard: { background: '#f8fafc', borderRadius: 14, padding: 28, border: '1px solid #e2e8f0' },
  featureIcon: { fontSize: 36, marginBottom: 14 },
  featureTitle: { fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 8 },
  featureDesc: { fontSize: 15, color: '#64748b', lineHeight: 1.6 },
  smsNote: { background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 14, padding: '24px 32px', maxWidth: 720, margin: '0 auto', textAlign: 'center' },
  smsNoteTitle: { fontSize: 18, fontWeight: 700, color: '#92400e', marginBottom: 8 },
  smsNoteText: { color: '#78350f', fontSize: 15, lineHeight: 1.6 },
  cta: { background: '#2563eb', padding: '64px 24px', textAlign: 'center' },
  ctaTitle: { fontSize: 32, fontWeight: 800, color: '#fff', marginBottom: 16 },
  ctaSub: { color: '#bfdbfe', fontSize: 18, marginBottom: 32 },
  ctaBtn: { background: '#fff', color: '#2563eb', border: 'none', padding: '14px 36px', borderRadius: 10, fontWeight: 700, fontSize: 17, cursor: 'pointer', textDecoration: 'none' },
  footer: { background: '#1e293b', color: '#94a3b8', padding: '32px 24px', textAlign: 'center', fontSize: 14 },
  footerLinks: { display: 'flex', gap: 20, justifyContent: 'center', marginBottom: 12, flexWrap: 'wrap' },
  footerLink: { color: '#94a3b8', textDecoration: 'none' },
};

const features = [
  { icon: '💊', title: 'Medication Tracking', desc: 'Track medications, dosages, and reminder times. Optional SMS reminders keep seniors on schedule.' },
  { icon: '📅', title: 'Appointment Management', desc: 'Schedule and manage doctor visits and appointments with optional SMS reminders 24h and 1h before.' },
  { icon: '📊', title: 'Health Vitals', desc: 'Log blood pressure, heart rate, weight, and glucose. View trends with interactive charts.' },
  { icon: '📝', title: 'Daily Care Journal', desc: 'Record mood, activity, appetite, and notes to track your loved one\'s daily well-being.' },
  { icon: '🚨', title: 'Emergency SOS', desc: 'One-tap emergency alert sends SMS to all assigned caregivers instantly. Premium feature.' },
  { icon: '👥', title: 'Caregiver Coordination', desc: 'Assign multiple caregivers to seniors. Everyone stays informed and coordinated.' },
];

export default function LandingPage() {
  return (
    <div style={s.page}>
      {/* Nav */}
      <nav style={s.nav}>
        <Link to="/" style={s.logo}>💙 SeniorCare</Link>
        <div style={s.navLinks}>
          <Link to="/login" style={s.loginBtn}>Log in</Link>
          <Link to="/register" style={s.registerBtn}>Get Started Free</Link>
        </div>
      </nav>

      {/* Hero */}
      <div style={s.hero}>
        <h1 style={s.heroTitle}>
          Care for your loved ones,<br />
          <span style={s.heroAccent}>with peace of mind.</span>
        </h1>
        <p style={s.heroSub}>
          SeniorCare helps family caregivers manage medications, appointments, and health for elderly loved ones — all in one place.
        </p>
        <div style={s.heroBtns}>
          <Link to="/register" style={s.primaryBtn}>Start for Free</Link>
          <Link to="/login" style={s.secondaryBtn}>Log In</Link>
        </div>
        <div style={s.freeNote}>Free plan available. No credit card required.</div>
      </div>

      {/* Features */}
      <div style={s.features}>
        <div style={s.featuresInner}>
          <h2 style={s.featuresTitle}>Everything you need to care for a senior</h2>
          <div style={s.grid}>
            {features.map(f => (
              <div key={f.title} style={s.featureCard}>
                <div style={s.featureIcon}>{f.icon}</div>
                <div style={s.featureTitle}>{f.title}</div>
                <div style={s.featureDesc}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SMS Info Banner */}
      <div style={{ padding: '48px 24px', background: '#f8fafc' }}>
        <div style={s.smsNote}>
          <div style={s.smsNoteTitle}>📱 About SMS Reminders</div>
          <p style={s.smsNoteText}>
            SMS reminders are an <strong>optional feature</strong>. You can use all of SeniorCare's features without enrolling in SMS.
            To enable SMS reminders, caregivers send an explicit invite from the app. Recipients must reply <strong>YES</strong> to consent before any messages are sent.
            Reply <strong>STOP</strong> at any time to unsubscribe. Message &amp; data rates may apply.
          </p>
          <Link to="/sms-opt-in" style={{ color: '#92400e', fontWeight: 600, fontSize: 14 }}>Learn more about our SMS program →</Link>
        </div>
      </div>

      {/* CTA */}
      <div style={s.cta}>
        <div style={s.ctaTitle}>Start caring smarter today</div>
        <div style={s.ctaSub}>Free plan included. Upgrade to Premium for unlimited access and SMS reminders.</div>
        <Link to="/register" style={s.ctaBtn}>Create Free Account</Link>
      </div>

      {/* Footer */}
      <footer style={s.footer}>
        <div style={s.footerLinks}>
          <Link to="/privacy" style={s.footerLink}>Privacy Policy</Link>
          <Link to="/terms" style={s.footerLink}>Terms of Service</Link>
          <Link to="/sms-opt-in" style={s.footerLink}>SMS Opt-In Info</Link>
          <a href="mailto:myseniorcare.support@gmail.com" style={s.footerLink}>Contact Support</a>
        </div>
        <div>© 2026 SeniorCare App · myseniorcare.org · support: myseniorcare.support@gmail.com</div>
      </footer>
    </div>
  );
}
